import classnames from 'classnames';
import LoadingDots from 'components/LoadingDots';
import React, { HTMLAttributes, RefObject } from 'react';
import { useTrekker } from 'utils/analytics';

const CSS = require('./RoundedButton.module.scss');

export type RoundedButtonProps = {
  isLoading?: boolean;
  children: any;
  size?: 'XS' | 'S' | 'M' | 'L';
  className?: string;
  inverted?: boolean;
  pink?: boolean;
  yellow?: boolean;
  sandstone?: boolean;
  jade?: boolean;
  danger?: boolean;
  onClick?: (event?: any) => void;
  type?: 'reset' | 'submit' | 'button';
  disabled?: boolean;
  fullWidth?: boolean;
  invertedBorder?: boolean;
  edgeRounding?: 'slight' | 'medium' | 'full';
  outlined?: boolean;
  elevated?: boolean;
} & HTMLAttributes<HTMLButtonElement>;

const RoundedButton = React.forwardRef<HTMLButtonElement, RoundedButtonProps>(
  (
    {
      disabled,
      isLoading,
      size = 'M',
      fullWidth,
      children,
      className,
      inverted,
      invertedBorder,
      onClick,
      pink,
      yellow,
      sandstone,
      jade,
      danger,
      type,
      edgeRounding = 'full',
      elevated,
      outlined,
      ...rest
    }: RoundedButtonProps,
    ref: RefObject<HTMLButtonElement>,
  ) => {
    const classes = classnames(CSS.Button, {
      [className]: className,
      [CSS.Inverted]: inverted,
      [CSS.InvertedBorder]: invertedBorder,
      [CSS.PinkButton]: pink,
      [CSS.YellowButton]: yellow,
      [CSS.SandstoneButton]: sandstone,
      [CSS.JadeButton]: jade,
      [CSS.DangerButton]: danger,
      [CSS.FullWidth]: fullWidth,
      [CSS.ExtraSmall]: size === 'XS',
      [CSS.Large]: size === 'L',
      [CSS.Small]: size === 'S',
      [CSS.EdgeRoundedSlight]: edgeRounding === 'slight',
      [CSS.EdgeRoundedMedium]: edgeRounding === 'medium',
      [CSS.OutlinedButton]: outlined,
      [CSS.ButtonElevated]: elevated,
    });
    const trekker = useTrekker();
    const handleClick = (e) => {
      if (typeof children === 'string') {
        trekker?.event('click', {
          component: ['RoundedButton'],
          buttonText: children,
        });
      } else {
        trekker?.event('click', { component: ['RoundedButton'] });
      }
      onClick?.(e);
    };
    return (
      <button
        ref={ref}
        type={type}
        className={classes}
        disabled={disabled || isLoading}
        onClick={handleClick}
        {...rest}
      >
        {!isLoading && children}
        {isLoading && (
          <LoadingDots
            isWhite={!(inverted || invertedBorder)}
            size={size === 'XS' || size === 'S' ? 's' : 'm'}
            className={CSS.Loading}
          />
        )}
      </button>
    );
  },
);

export default RoundedButton;
